import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type {
    AssistantMessage,
    ConfirmEquipmentParams,
    DocUrlResponse,
    EquipmentCandidate,
    ExpertChatMessageItem,
    ExpertErrorCode,
    FlowAnswerPayload,
    FlowNode,
    FlowRun,
    FlowStepResponse,
    FlowSuggestion,
    FlowTerminal,
    InputMode,
    RecentSessionItem,
    SendMessageOptions,
    Session,
    SessionOutcome,
    StartSessionParams,
    StartSessionResponse,
    TechLineSummaryResponse,
} from './types';

export const MESSAGE_CLIENT_TIMEOUT_MS = 60_000;
export const MAX_SESSION_TITLE_CHARS = 60;

interface PendingMessageDraft {
    clientMessageId: string;
    text: string;
    imagePath?: string;
    inputMode: InputMode;
}

/** Generate an RFC4122 v4 UUID for idempotent `client_message_id` requests. */
export function generateClientMessageId(): string {
    return crypto.randomUUID();
}

export function truncateSessionTitle(raw: string | null | undefined, fallback = 'Expert chat'): string {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return fallback;
    if (trimmed.length <= MAX_SESSION_TITLE_CHARS) return trimmed;
    return `${trimmed.slice(0, MAX_SESSION_TITLE_CHARS)}…`;
}

/** Map fixed contract error codes (400/401/403/404/409/429/500/TIMEOUT) to clear Australian English copy. */
export function mapExpertChatError(
    rawMessage: string | null | undefined,
    code?: string | null,
    status?: number,
): { message: string; code: ExpertErrorCode | null } {
    const normalizedCode = code?.trim().toUpperCase() || null;

    if (
        normalizedCode === 'TIMEOUT' ||
        status === 408 ||
        (rawMessage && /timed?\s*out|abort/i.test(rawMessage))
    ) {
        return {
            code: 'TIMEOUT',
            message:
                rawMessage && /timed?\s*out/i.test(rawMessage)
                    ? rawMessage
                    : 'The request timed out after 60 seconds. Please click Retry to try again.',
        };
    }
    if (
        normalizedCode === 'EXPERT_NOT_ENABLED' ||
        (status === 403 && rawMessage?.includes('EXPERT_NOT_ENABLED'))
    ) {
        return {
            code: 'EXPERT_NOT_ENABLED',
            message: 'The Technical Expert is not switched on for your account',
        };
    }
    if (normalizedCode === 'MANUFACTURER_NOT_AVAILABLE') {
        return {
            code: 'MANUFACTURER_NOT_AVAILABLE',
            message: "This manufacturer's technical expert isn't available yet.",
        };
    }
    if (normalizedCode === 'FORBIDDEN' || status === 403) {
        return {
            code: normalizedCode || 'FORBIDDEN',
            message: 'You do not have access to this technical expert session.',
        };
    }
    if (
        normalizedCode === 'RATE_LIMITED' ||
        status === 429 ||
        rawMessage?.includes('RATE_LIMITED')
    ) {
        return {
            code: 'RATE_LIMITED',
            message: 'Too many requests right now. Please wait a moment and try again.',
        };
    }
    if (normalizedCode === 'UNAUTHORIZED' || status === 401) {
        return {
            code: 'UNAUTHORIZED',
            message: 'Your session has expired. Please sign in again.',
        };
    }
    if (normalizedCode === 'NOT_FOUND' || status === 404) {
        return {
            code: 'NOT_FOUND',
            message: 'That session or document could not be found.',
        };
    }
    if (normalizedCode === 'MESSAGE_IN_PROGRESS') {
        return {
            code: 'MESSAGE_IN_PROGRESS',
            message: 'Still working on your answer…',
        };
    }
    if (normalizedCode === 'MESSAGE_ABANDONED') {
        return {
            code: 'MESSAGE_ABANDONED',
            message: 'That request did not finish. Please try again.',
        };
    }
    if (normalizedCode === 'CONFLICT' || status === 409) {
        return {
            code: normalizedCode || 'CONFLICT',
            message: 'This session was updated elsewhere. Please refresh and try again.',
        };
    }
    if (normalizedCode === 'INVALID_INPUT' || status === 400) {
        return {
            code: 'INVALID_INPUT',
            message:
                rawMessage && rawMessage !== 'INVALID_INPUT'
                    ? rawMessage
                    : 'Please check your input and try again.',
        };
    }
    if (normalizedCode === 'INTERNAL' || status === 500) {
        return {
            code: 'INTERNAL',
            message:
                rawMessage &&
                rawMessage !== 'INTERNAL' &&
                rawMessage !== 'Edge Function returned a non-2xx status code'
                    ? rawMessage
                    : 'Something went wrong on our end. Please try again shortly.',
        };
    }

    return {
        code: normalizedCode,
        message:
            rawMessage?.trim() &&
            rawMessage.trim() !== 'Edge Function returned a non-2xx status code'
                ? rawMessage.trim()
                : 'Connection error. Please check your connection and try again.',
    };
}

export async function extractInvokeErrorDetails(
    err: unknown,
    data?: unknown,
): Promise<{ rawMessage: string | null; code: string | null; status?: number }> {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const d = data as Record<string, unknown>;
        if (typeof d.error === 'string' || typeof d.code === 'string') {
            return {
                rawMessage:
                    typeof d.error === 'string'
                        ? d.error
                        : typeof d.message === 'string'
                          ? d.message
                          : null,
                code: typeof d.code === 'string' ? d.code : null,
                status: typeof d.status === 'number' ? d.status : undefined,
            };
        }
    }

    if (typeof err === 'string') {
        return { rawMessage: err, code: null };
    }

    if (err && typeof err === 'object') {
        const errObj = err as Record<string, unknown>;
        const ctx = errObj.context;

        if (ctx && typeof ctx === 'object') {
            const ctxObj = ctx as {
                status?: number;
                json?: () => Promise<unknown>;
                text?: () => Promise<string>;
                clone?: () => { json?: () => Promise<unknown>; text?: () => Promise<string> };
            };
            const status = typeof ctxObj.status === 'number' ? ctxObj.status : undefined;
            try {
                const reader = typeof ctxObj.clone === 'function' ? ctxObj.clone() : ctxObj;
                if (typeof reader.text === 'function') {
                    const txt = await reader.text();
                    if (txt) {
                        try {
                            const parsed = JSON.parse(txt) as Record<string, unknown>;
                            return {
                                rawMessage:
                                    typeof parsed.error === 'string'
                                        ? parsed.error
                                        : typeof parsed.message === 'string'
                                          ? parsed.message
                                          : txt,
                                code: typeof parsed.code === 'string' ? parsed.code : null,
                                status,
                            };
                        } catch {
                            return { rawMessage: txt, code: null, status };
                        }
                    }
                } else if (typeof reader.json === 'function') {
                    const parsed = (await reader.json()) as Record<string, unknown>;
                    if (parsed && typeof parsed === 'object') {
                        return {
                            rawMessage:
                                typeof parsed.error === 'string'
                                    ? parsed.error
                                    : typeof parsed.message === 'string'
                                      ? parsed.message
                                      : null,
                            code: typeof parsed.code === 'string' ? parsed.code : null,
                            status,
                        };
                    }
                }
            } catch {
                // Fall through
            }
        }

        const code =
            typeof errObj.code === 'string'
                ? errObj.code
                : errObj.name === 'AbortError'
                  ? 'TIMEOUT'
                  : null;
        const status = typeof errObj.status === 'number' ? errObj.status : undefined;
        const rawMessage =
            typeof errObj.error === 'string'
                ? errObj.error
                : typeof errObj.message === 'string'
                  ? errObj.message
                  : null;

        return { rawMessage, code, status };
    }

    return { rawMessage: null, code: null };
}

export interface UseExpertChatOptions {
    sessionId?: string | null;
    autoLoadHistory?: boolean;
    initialDraft?: string;
    messageTimeoutMs?: number;
}

export function useExpertChat(options?: UseExpertChatOptions) {
    const initialSessionId = options?.sessionId ?? null;
    const autoLoadHistory = options?.autoLoadHistory ?? Boolean(initialSessionId);
    const messageTimeoutMs = options?.messageTimeoutMs ?? MESSAGE_CLIENT_TIMEOUT_MS;

    const [session, setSession] = useState<Session | null>(null);
    const [equipmentCandidates, setEquipmentCandidates] = useState<EquipmentCandidate[]>([]);
    const [needsConfirmation, setNeedsConfirmation] = useState<boolean>(false);
    const [standardsEnabled, setStandardsEnabled] = useState<boolean>(true);
    const [messages, setMessages] = useState<ExpertChatMessageItem[]>([]);
    const [recentSessions, setRecentSessions] = useState<RecentSessionItem[]>([]);
    const [flowSuggestions, setFlowSuggestions] = useState<FlowSuggestion[]>([]);
    const [activeFlowRun, setActiveFlowRun] = useState<FlowRun | null>(null);
    const [activeFlowNode, setActiveFlowNode] = useState<FlowNode | null>(null);
    const [activeFlowTerminal, setActiveFlowTerminal] = useState<FlowTerminal | null>(null);
    const [isFlowLoading, setIsFlowLoading] = useState<boolean>(false);
    const [flowNeedsClarification, setFlowNeedsClarification] = useState<boolean>(false);
    const activeFlowRunRef = useRef<FlowRun | null>(null);
    activeFlowRunRef.current = activeFlowRun;

    const [draft, setDraftState] = useState<string>(options?.initialDraft ?? '');
    const [attachedImagePath, setAttachedImagePath] = useState<string | null>(null);
    const [techLineSummary, setTechLineSummary] = useState<TechLineSummaryResponse | null>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [isLoadingTechLine, setIsLoadingTechLine] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<ExpertErrorCode | null>(null);
    const [pendingClientMessageId, setPendingClientMessageId] = useState<string | null>(null);
    const [lastFailedAction, setLastFailedAction] = useState<(() => Promise<unknown>) | null>(
        null,
    );

    const pendingDraftRef = useRef<PendingMessageDraft | null>(null);
    const sessionRef = useRef<Session | null>(session);
    sessionRef.current = session;

    const setDraft = useCallback((nextText: string) => {
        setDraftState(nextText);
        if (pendingDraftRef.current && nextText !== pendingDraftRef.current.text) {
            pendingDraftRef.current = null;
            setPendingClientMessageId(null);
        }
    }, []);

    const callExpertAction = useCallback(
        async <T,>(action: string, payload: Record<string, unknown> = {}): Promise<T> => {
            const body = {
                action,
                ...payload,
            };

            let timeoutId: ReturnType<typeof setTimeout> | null = null;

            try {
                const invokePromise = supabase.functions.invoke('expert-chat', {
                    body,
                });

                const res =
                    action === 'message'
                        ? await Promise.race([
                              invokePromise,
                              new Promise<never>((_, reject) => {
                                  timeoutId = setTimeout(() => {
                                      const timeoutErr = new Error(
                                          'The request timed out after 60 seconds. Please click Retry to try again.',
                                      ) as Error & { code?: string };
                                      timeoutErr.code = 'TIMEOUT';
                                      reject(timeoutErr);
                                  }, messageTimeoutMs);
                              }),
                          ])
                        : await invokePromise;

                if (res?.error) {
                    const details = await extractInvokeErrorDetails(res.error, res.data);
                    const mapped = mapExpertChatError(
                        details.rawMessage,
                        details.code,
                        details.status,
                    );
                    const err = new Error(mapped.message) as Error & {
                        code?: string | null;
                        status?: number;
                        __mapped?: boolean;
                    };
                    err.code = mapped.code;
                    err.status = details.status;
                    err.__mapped = true;
                    throw err;
                }

                const data = res?.data;
                if (
                    data &&
                    typeof data === 'object' &&
                    !Array.isArray(data) &&
                    'error' in (data as Record<string, unknown>) &&
                    typeof (data as Record<string, unknown>).error === 'string' &&
                    !('session' in (data as Record<string, unknown>))
                ) {
                    const details = await extractInvokeErrorDetails(null, data);
                    const mapped = mapExpertChatError(
                        details.rawMessage,
                        details.code,
                        details.status,
                    );
                    const err = new Error(mapped.message) as Error & {
                        code?: string | null;
                        status?: number;
                        __mapped?: boolean;
                    };
                    err.code = mapped.code;
                    err.status = details.status;
                    err.__mapped = true;
                    throw err;
                }

                if (!data) {
                    const mapped = mapExpertChatError(null, null, undefined);
                    const err = new Error(mapped.message) as Error & {
                        code?: string | null;
                        __mapped?: boolean;
                    };
                    err.code = mapped.code;
                    err.__mapped = true;
                    throw err;
                }

                return data as T;
            } catch (thrown: unknown) {
                if (
                    thrown &&
                    typeof thrown === 'object' &&
                    (thrown as { __mapped?: boolean }).__mapped === true
                ) {
                    throw thrown;
                }
                const details = await extractInvokeErrorDetails(thrown);
                const mapped = mapExpertChatError(
                    details.rawMessage,
                    details.code,
                    details.status,
                );
                const err = new Error(mapped.message) as Error & {
                    code?: string | null;
                    status?: number;
                };
                err.code = mapped.code;
                err.status = details.status;
                throw err;
            } finally {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId);
                }
            }
        },
        [messageTimeoutMs],
    );

    // 1. start
    const startSession = useCallback(
        async (params?: StartSessionParams): Promise<StartSessionResponse | null> => {
            setIsLoading(true);
            setError(null);
            setErrorCode(null);
            setLastFailedAction(null);
            try {
                const payload: Record<string, unknown> = {
                    source_surface: params?.source_surface ?? 'EXPERT_HOME',
                };
                if (params?.manufacturer_id && params.manufacturer_id.trim()) {
                    payload.manufacturer_id = params.manufacturer_id.trim();
                }
                if (params?.series_id && params.series_id.trim()) {
                    payload.series_id = params.series_id.trim();
                }
                if (params?.model_id && params.model_id.trim()) {
                    payload.model_id = params.model_id.trim();
                }
                if (params?.model_text && params.model_text.trim()) {
                    payload.model_text = params.model_text.trim();
                }
                if (params?.job_id && params.job_id.trim()) {
                    payload.job_id = params.job_id.trim();
                }
                if (params?.tradie_workflow_id && params.tradie_workflow_id.trim()) {
                    payload.tradie_workflow_id = params.tradie_workflow_id.trim();
                }
                if (params?.workflow_step_id && params.workflow_step_id.trim()) {
                    payload.workflow_step_id = params.workflow_step_id.trim();
                }

                const data = await callExpertAction<StartSessionResponse>('start', payload);
                setSession(data.session);
                sessionRef.current = data.session;
                setEquipmentCandidates(data.equipment_candidates ?? []);
                setNeedsConfirmation(Boolean(data.needs_confirmation));
                if (typeof data.standards_enabled === 'boolean') {
                    setStandardsEnabled(data.standards_enabled);
                }
                setMessages([]);
                pendingDraftRef.current = null;
                setPendingClientMessageId(null);
                return data;
            } catch (err: unknown) {
                const msg =
                    err instanceof Error ? err.message : 'Could not start expert session.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                setLastFailedAction(() => () => startSession(params));
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [callExpertAction],
    );

    // 2. confirm_equipment
    const confirmEquipment = useCallback(
        async (params: ConfirmEquipmentParams): Promise<Session | null> => {
            const targetSessionId =
                params.session_id ?? sessionRef.current?.id ?? initialSessionId;
            if (!targetSessionId) return null;

            setIsLoading(true);
            setError(null);
            setErrorCode(null);
            setLastFailedAction(null);
            try {
                const payload: Record<string, unknown> = {
                    session_id: targetSessionId,
                    manufacturer_id: params.manufacturer_id,
                };
                if (params.series_id !== undefined) payload.series_id = params.series_id;
                if (params.model_id !== undefined) payload.model_id = params.model_id;
                if (params.model_text !== undefined) payload.model_text = params.model_text;
                if (params.serial_number !== undefined)
                    payload.serial_number = params.serial_number;
                if (params.error_code !== undefined) payload.error_code = params.error_code;
                if (params.firmware_version !== undefined)
                    payload.firmware_version = params.firmware_version;

                const data = await callExpertAction<{ session: Session }>(
                    'confirm_equipment',
                    payload,
                );
                setSession(data.session);
                sessionRef.current = data.session;
                setNeedsConfirmation(false);
                return data.session;
            } catch (err: unknown) {
                const msg =
                    err instanceof Error ? err.message : 'Could not confirm equipment.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                setLastFailedAction(() => () => confirmEquipment(params));
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [callExpertAction, initialSessionId],
    );

    // 4. message (idempotent via client_message_id; keeps draft and reuses client_message_id on retry)
    const sendMessage = useCallback(
        async (
            textArg?: string,
            sendOptions?: SendMessageOptions & {
                sessionId?: string;
                manufacturerId?: string | null;
            },
        ): Promise<{
            user_message: { id: string; created_at: string };
            assistant_message: AssistantMessage;
            session: Session;
        } | null> => {
            const rawText = textArg !== undefined ? textArg : draft;
            const trimmed = rawText.trim();
            if (!trimmed || isSending) return null;

            const existingPending = pendingDraftRef.current;
            const isRetryingSameDraft =
                existingPending !== null && existingPending.text === trimmed;

            const clientMessageId =
                sendOptions?.client_message_id ??
                (isRetryingSameDraft
                    ? existingPending.clientMessageId
                    : generateClientMessageId());

            const effectiveImagePath =
                sendOptions?.image_path !== undefined
                    ? sendOptions.image_path
                    : isRetryingSameDraft && existingPending.imagePath
                      ? existingPending.imagePath
                      : attachedImagePath ?? undefined;

            const effectiveInputMode: InputMode =
                sendOptions?.input_mode ??
                (effectiveImagePath
                    ? 'PHOTO'
                    : isRetryingSameDraft
                      ? existingPending.inputMode
                      : 'TEXT');

            // Store in pendingDraftRef BEFORE network request so if it fails/times out, retry reuses clientMessageId
            pendingDraftRef.current = {
                clientMessageId,
                text: trimmed,
                imagePath: effectiveImagePath,
                inputMode: effectiveInputMode,
            };
            setPendingClientMessageId(clientMessageId);
            setDraftState(trimmed);
            setIsSending(true);
            setError(null);
            setErrorCode(null);
            setLastFailedAction(null);

            try {
                let targetSessionId =
                    sendOptions?.sessionId ?? sessionRef.current?.id ?? initialSessionId;

                if (!targetSessionId) {
                    const startPayload: Record<string, unknown> = {
                        source_surface: 'EXPERT_HOME',
                    };
                    if (sendOptions?.manufacturerId && sendOptions.manufacturerId.trim()) {
                        startPayload.manufacturer_id = sendOptions.manufacturerId.trim();
                    }
                    const started = await callExpertAction<StartSessionResponse>(
                        'start',
                        startPayload,
                    );
                    setSession(started.session);
                    sessionRef.current = started.session;
                    setEquipmentCandidates(started.equipment_candidates ?? []);
                    setNeedsConfirmation(Boolean(started.needs_confirmation));
                    if (typeof started.standards_enabled === 'boolean') {
                        setStandardsEnabled(started.standards_enabled);
                    }
                    targetSessionId = started.session.id;
                }

                const MAX_IN_PROGRESS_RETRIES = 6;
                const IN_PROGRESS_POLL_INTERVAL_MS = 5_000;
                let activeClientMessageId = clientMessageId;
                let abandonedRetried = false;
                let inProgressRetries = 0;

                while (true) {
                    const payload: Record<string, unknown> = {
                        session_id: targetSessionId,
                        client_message_id: activeClientMessageId,
                        text: trimmed,
                        input_mode: effectiveInputMode,
                    };
                    if (effectiveImagePath) {
                        payload.image_path = effectiveImagePath;
                    }

                    try {
                        const data = await callExpertAction<{
                            user_message: { id: string; created_at: string };
                            assistant_message: AssistantMessage;
                            session: Session;
                            suggested_flows?: FlowSuggestion[];
                        }>('message', payload);

                        // Success: clear pending retry state and draft
                        pendingDraftRef.current = null;
                        setPendingClientMessageId(null);
                        setDraftState('');
                        setAttachedImagePath(null);
                        setError(null);
                        setErrorCode(null);
                        setLastFailedAction(null);
                        setSession(data.session);
                        sessionRef.current = data.session;
                        if (Array.isArray(data.suggested_flows) && data.suggested_flows.length > 0) {
                            setFlowSuggestions((prev) => {
                                const merged = [...prev];
                                for (const item of data.suggested_flows!) {
                                    const idx = merged.findIndex((s) => s.flow_id === item.flow_id);
                                    if (idx >= 0) {
                                        merged[idx] = item;
                                    } else {
                                        merged.push(item);
                                    }
                                }
                                return merged;
                            });
                        }

                        const userMsgItem: ExpertChatMessageItem = {
                            id: data.user_message.id,
                            role: 'USER',
                            content: trimmed,
                            created_at: data.user_message.created_at,
                            image_paths: effectiveImagePath ? [effectiveImagePath] : [],
                            feedback_rating: null,
                        };

                        const assistantMsgItem: ExpertChatMessageItem = {
                            id: data.assistant_message.id,
                            role: 'ASSISTANT',
                            content: data.assistant_message.content_md,
                            created_at: data.assistant_message.created_at,
                            assistant: data.assistant_message,
                            image_paths: [],
                            feedback_rating: null,
                        };

                        setMessages((prev) => {
                            const withoutDupes = prev.filter(
                                (m) =>
                                    m.id !== userMsgItem.id &&
                                    m.id !== assistantMsgItem.id &&
                                    !m.id.startsWith('pending-user-'),
                            );
                            return [...withoutDupes, userMsgItem, assistantMsgItem];
                        });

                        // Keep recentSessions up to date (newest first; title = first user message truncated)
                        setRecentSessions((prev) => {
                            const existing = prev.find((s) => s.id === data.session.id);
                            const title = existing?.title && existing.title !== 'Expert chat'
                                ? existing.title
                                : truncateSessionTitle(trimmed, data.session.model_label || 'Expert chat');
                            const updatedItem: RecentSessionItem = {
                                ...data.session,
                                last_message_at: data.assistant_message.created_at,
                                title,
                            };
                            const rest = prev.filter((s) => s.id !== data.session.id);
                            return [updatedItem, ...rest];
                        });

                        return data;
                    } catch (attemptErr: unknown) {
                        const code = (attemptErr as { code?: string | null })?.code ?? null;

                        if (code === 'MESSAGE_IN_PROGRESS') {
                            const optimisticId = `pending-user-${activeClientMessageId}`;
                            setMessages((prev) => {
                                if (
                                    prev.some(
                                        (m) =>
                                            m.id === optimisticId ||
                                            (m.role === 'USER' && m.content === trimmed),
                                    )
                                ) {
                                    return prev;
                                }
                                return [
                                    ...prev,
                                    {
                                        id: optimisticId,
                                        role: 'USER',
                                        content: trimmed,
                                        created_at: new Date().toISOString(),
                                        image_paths: effectiveImagePath
                                            ? [effectiveImagePath]
                                            : [],
                                        feedback_rating: null,
                                    },
                                ];
                            });

                            if (inProgressRetries < MAX_IN_PROGRESS_RETRIES) {
                                inProgressRetries += 1;
                                setError('Still working on your answer…');
                                setErrorCode('MESSAGE_IN_PROGRESS');
                                await new Promise<void>((resolve) => {
                                    setTimeout(resolve, IN_PROGRESS_POLL_INTERVAL_MS);
                                });
                                continue;
                            }

                            const timeoutErr = new Error(
                                'The request timed out. Please click Retry to try again.',
                            ) as Error & { code?: string | null };
                            timeoutErr.code = 'TIMEOUT';
                            throw timeoutErr;
                        }

                        if (code === 'MESSAGE_ABANDONED' && !abandonedRetried) {
                            abandonedRetried = true;
                            inProgressRetries = 0;
                            const nextClientMessageId = generateClientMessageId();
                            activeClientMessageId = nextClientMessageId;
                            pendingDraftRef.current = {
                                clientMessageId: nextClientMessageId,
                                text: trimmed,
                                imagePath: effectiveImagePath,
                                inputMode: effectiveInputMode,
                            };
                            setPendingClientMessageId(nextClientMessageId);
                            continue;
                        }

                        throw attemptErr;
                    }
                }
            } catch (err: unknown) {
                const rawMsg =
                    err instanceof Error ? err.message : 'Message failed to send.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(rawMsg);
                setErrorCode(code);
                return null;
            } finally {
                setIsSending(false);
            }
        },
        [attachedImagePath, callExpertAction, draft, initialSessionId, isSending],
    );

    const retryLastMessage = useCallback(async () => {
        const pending = pendingDraftRef.current;
        if (pending) {
            return sendMessage(pending.text, {
                client_message_id: pending.clientMessageId,
                image_path: pending.imagePath,
                input_mode: pending.inputMode,
            });
        }
        if (lastFailedAction) {
            return lastFailedAction();
        }
        return null;
    }, [lastFailedAction, sendMessage]);

    const dismissFlow = useCallback((flowId?: string) => {
        if (flowId) {
            setFlowSuggestions((prev) => prev.filter((s) => s.flow_id !== flowId));
        }
        setActiveFlowRun(null);
        activeFlowRunRef.current = null;
        setActiveFlowNode(null);
        setActiveFlowTerminal(null);
        setFlowNeedsClarification(false);
    }, []);

    // 5. history
    const loadHistory = useCallback(
        async (overrideSessionId?: string): Promise<ExpertChatMessageItem[] | null> => {
            const targetSessionId =
                overrideSessionId ?? sessionRef.current?.id ?? initialSessionId;
            if (!targetSessionId) return null;

            setIsLoading(true);
            setError(null);
            setErrorCode(null);
            setLastFailedAction(null);
            pendingDraftRef.current = null;
            setPendingClientMessageId(null);
            setFlowSuggestions([]);
            dismissFlow();
            try {
                const data = await callExpertAction<{
                    session: Session;
                    messages: ExpertChatMessageItem[];
                }>('history', { session_id: targetSessionId });

                setSession(data.session);
                sessionRef.current = data.session;
                setMessages(data.messages ?? []);
                return data.messages ?? [];
            } catch (err: unknown) {
                const msg =
                    err instanceof Error ? err.message : 'Could not load session history.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                setLastFailedAction(() => () => loadHistory(targetSessionId));
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [callExpertAction, dismissFlow, initialSessionId],
    );

    // 6. recent_sessions
    const loadRecentSessions = useCallback(
        async (limit = 20): Promise<RecentSessionItem[]> => {
            const clampedLimit = Math.max(1, Math.min(20, limit));
            setIsLoading(true);
            setError(null);
            setErrorCode(null);
            try {
                const data = await callExpertAction<{ sessions?: RecentSessionItem[] }>(
                    'recent_sessions',
                    { limit: clampedLimit },
                );
                if (!Array.isArray(data?.sessions)) {
                    return [];
                }
                const normalized = [...data.sessions]
                    .map((item) => ({
                        ...item,
                        title: truncateSessionTitle(
                            item.title,
                            item.model_label || 'Expert chat',
                        ),
                    }))
                    .sort((a, b) => {
                        const aTime = a.last_message_at ?? a.created_at ?? '';
                        const bTime = b.last_message_at ?? b.created_at ?? '';
                        if (!aTime && !bTime) return 0;
                        return bTime.localeCompare(aTime);
                    });
                setRecentSessions(normalized);
                return normalized;
            } catch (err: unknown) {
                const msg =
                    err instanceof Error ? err.message : 'Could not load recent sessions.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                setLastFailedAction(() => () => loadRecentSessions(limit));
                return [];
            } finally {
                setIsLoading(false);
            }
        },
        [callExpertAction],
    );

    // 7. doc_url
    const fetchDocUrl = useCallback(
        async (
            documentId: string,
            pageNumber?: number,
            overrideSessionId?: string,
        ): Promise<DocUrlResponse> => {
            const targetSessionId =
                overrideSessionId ?? sessionRef.current?.id ?? initialSessionId;
            if (!targetSessionId) {
                return { kind: 'NONE', url: null };
            }

            try {
                const payload: Record<string, unknown> = {
                    session_id: targetSessionId,
                    document_id: documentId,
                };
                if (typeof pageNumber === 'number' && pageNumber >= 1) {
                    payload.page_number = pageNumber;
                }
                return await callExpertAction<DocUrlResponse>('doc_url', payload);
            } catch (err: unknown) {
                const msg =
                    err instanceof Error ? err.message : 'Could not open source document.';
                setError(msg);
                return { kind: 'NONE', url: null };
            }
        },
        [callExpertAction, initialSessionId],
    );

    // 8. tech_line_summary
    const fetchTechLineSummary = useCallback(
        async (overrideSessionId?: string): Promise<TechLineSummaryResponse | null> => {
            const targetSessionId =
                overrideSessionId ?? sessionRef.current?.id ?? initialSessionId;
            if (!targetSessionId) return null;

            setIsLoadingTechLine(true);
            try {
                const data = await callExpertAction<TechLineSummaryResponse>(
                    'tech_line_summary',
                    {
                        session_id: targetSessionId,
                    },
                );
                setTechLineSummary(data);
                return data;
            } catch (err: unknown) {
                const msg =
                    err instanceof Error ? err.message : 'Could not load tech line summary.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                return null;
            } finally {
                setIsLoadingTechLine(false);
            }
        },
        [callExpertAction, initialSessionId],
    );

    // 9. close
    const closeSession = useCallback(
        async (
            outcome: SessionOutcome,
            note?: string,
            overrideSessionId?: string,
        ): Promise<Session | null> => {
            const targetSessionId =
                overrideSessionId ?? sessionRef.current?.id ?? initialSessionId;
            if (!targetSessionId) return null;

            setIsLoading(true);
            setError(null);
            setErrorCode(null);
            try {
                const payload: Record<string, unknown> = {
                    session_id: targetSessionId,
                    outcome,
                };
                if (note && note.trim()) {
                    payload.note = note.trim();
                }
                const data = await callExpertAction<{ session: Session }>('close', payload);
                setSession(data.session);
                sessionRef.current = data.session;
                return data.session;
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'Could not close session.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [callExpertAction, initialSessionId],
    );

    // 10. Feedback RPC: set_expert_message_feedback(p_message_id, p_rating, p_comment)
    const submitFeedback = useCallback(
        async (messageId: string, rating: -1 | 1, comment?: string): Promise<boolean> => {
            setMessages((prev) =>
                prev.map((m) => (m.id === messageId ? { ...m, feedback_rating: rating } : m)),
            );

            try {
                const rpcArgs: {
                    p_message_id: string;
                    p_rating: number;
                    p_comment?: string;
                } = {
                    p_message_id: messageId,
                    p_rating: rating,
                };
                if (comment && comment.trim()) {
                    rpcArgs.p_comment = comment.trim();
                }
                const { error: rpcError } = await supabase.rpc(
                    'set_expert_message_feedback',
                    rpcArgs,
                );
                if (rpcError) {
                    throw new Error(rpcError.message || 'Could not save feedback.');
                }
                return true;
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'Could not save feedback.';
                setError(msg);
                return false;
            }
        },
        [],
    );

    const applyFlowState = useCallback((data: FlowStepResponse | null | undefined) => {
        if (!data) return false;
        if (data.run) {
            setActiveFlowRun(data.run);
            activeFlowRunRef.current = data.run;
        }
        if (data.terminal) {
            setActiveFlowTerminal(data.terminal);
            setActiveFlowNode(null);
            return true;
        }
        if (data.node) {
            setActiveFlowNode(data.node);
            setActiveFlowTerminal(null);
            return true;
        }
        return false;
    }, []);

    const startFlow = useCallback(
        async (flowId: string): Promise<FlowStepResponse | null> => {
            const sid = sessionRef.current?.id ?? initialSessionId;
            if (!sid) return null;

            setIsFlowLoading(true);
            setError(null);
            setErrorCode(null);
            try {
                const data = await callExpertAction<FlowStepResponse>('flow_start', {
                    session_id: sid,
                    flow_id: flowId,
                });
                applyFlowState(data);
                setFlowSuggestions((prev) => prev.filter((s) => s.flow_id !== flowId));
                return data;
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'Could not start flow.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                return null;
            } finally {
                setIsFlowLoading(false);
            }
        },
        [applyFlowState, callExpertAction, initialSessionId],
    );

    const answerFlow = useCallback(
        async (payload: FlowAnswerPayload): Promise<FlowStepResponse | null> => {
            const runId = payload.run_id ?? activeFlowRunRef.current?.id;
            if (!runId) return null;

            setIsFlowLoading(true);
            setError(null);
            setErrorCode(null);
            try {
                const reqPayload: Record<string, unknown> = {
                    run_id: runId,
                    node_id: payload.node_id,
                };
                if (payload.option_id !== undefined) reqPayload.option_id = payload.option_id;
                if (payload.answer_text !== undefined) reqPayload.answer_text = payload.answer_text;
                if (payload.reading !== undefined) reqPayload.reading = payload.reading;
                if (payload.image_path !== undefined) reqPayload.image_path = payload.image_path;

                const data = await callExpertAction<FlowStepResponse>('flow_answer', reqPayload);
                applyFlowState(data);
                setFlowNeedsClarification(Boolean(data.needs_clarification));
                return data;
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'Could not submit answer.';
                const code = (err as { code?: string | null })?.code ?? null;
                setError(msg);
                setErrorCode(code);
                return null;
            } finally {
                setIsFlowLoading(false);
            }
        },
        [applyFlowState, callExpertAction],
    );

    const stepBackFlow = useCallback(async (): Promise<FlowStepResponse | null> => {
        const runId = activeFlowRunRef.current?.id;
        if (!runId) return null;

        setIsFlowLoading(true);
        setError(null);
        setErrorCode(null);
        try {
            const data = await callExpertAction<FlowStepResponse>('flow_back', { run_id: runId });
            applyFlowState(data);
            return data;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Could not step back.';
            const code = (err as { code?: string | null })?.code ?? null;
            setError(msg);
            setErrorCode(code);
            return null;
        } finally {
            setIsFlowLoading(false);
        }
    }, [applyFlowState, callExpertAction]);

    const abandonFlow = useCallback(async (): Promise<boolean> => {
        const runId = activeFlowRunRef.current?.id;
        dismissFlow();

        if (!runId) return true;
        try {
            await callExpertAction<unknown>('flow_abandon', { run_id: runId });
            return true;
        } catch {
            return false;
        }
    }, [callExpertAction, dismissFlow]);

    const fetchFlowSummary = useCallback(async (): Promise<string | null> => {
        const runId = activeFlowRunRef.current?.id;
        if (!runId) return null;

        try {
            const data = await callExpertAction<{ summary_md?: string }>('flow_summary', {
                run_id: runId,
            });
            return data.summary_md ?? null;
        } catch {
            return null;
        }
    }, [callExpertAction]);

    const resetToNewConversation = useCallback(() => {
        setSession(null);
        sessionRef.current = null;
        setMessages([]);
        setEquipmentCandidates([]);
        setNeedsConfirmation(false);
        setError(null);
        setErrorCode(null);
        setLastFailedAction(null);
        pendingDraftRef.current = null;
        setPendingClientMessageId(null);
        setFlowSuggestions([]);
        dismissFlow();
    }, [dismissFlow]);

    useEffect(() => {
        if (autoLoadHistory && initialSessionId) {
            void loadHistory(initialSessionId);
        }
    }, [autoLoadHistory, initialSessionId, loadHistory]);

    return {
        session,
        equipmentCandidates,
        needsConfirmation,
        standardsEnabled,
        messages,
        recentSessions,
        draft,
        setDraft,
        attachedImagePath,
        setAttachedImagePath,
        techLineSummary,
        isLoading,
        isSending,
        isLoadingTechLine,
        error,
        errorCode,
        pendingClientMessageId,
        canRetry: pendingClientMessageId !== null || lastFailedAction !== null,
        flowSuggestions,
        activeFlowRun,
        activeFlowNode,
        activeFlowTerminal,
        isFlowLoading,
        flowNeedsClarification,
        startFlow,
        answerFlow,
        stepBackFlow,
        abandonFlow,
        dismissFlow,
        fetchFlowSummary,
        startSession,
        confirmEquipment,
        sendMessage,
        retryLastMessage,
        loadHistory,
        loadRecentSessions,
        fetchDocUrl,
        fetchTechLineSummary,
        closeSession,
        submitFeedback,
        resetToNewConversation,
    };
}

export default useExpertChat;
